import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import { useGetConsultViewDetailsById } from '../../API/hooks/ConsultsHook';
import { useNavigate, useLocation, useParams } from 'react-router';
import BackIcon from '@assets/images/BackIcon.svg';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import {
  CONSULTS_ROUTE_PATH,
  NEW_CONSULTS_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomDivider from '@qler-view/shared_lib/src/shared/lib/ui-components/Divider/CustomDivider';
import ChatQLER from '@qler-view/shared_lib/src/shared/Chat/ChatQLER';
import ChatIcon from '@assets/images/chat.svg';
import {
  useGetAcsChatToken,
  useGetConsultChatThread,
} from '../../API/hooks/acsChatHook';
import { getUserFromStorage } from '../login/storageUtility';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src/shared/constants/portalType';
import CustomCard from '@qler-view/shared_lib/src/shared/lib/ui-components/Card/CustomCard';
import CustomCardContent from '@qler-view/shared_lib/src/shared/lib/ui-components/Card/CustomCardContent';
import CustomTabs from '@qler-view/shared_lib/src/shared/lib/ui-components/Tabs/CustomTabs';
import CustomTabPanel from '@qler-view/shared_lib/src/shared/lib/ui-components/Tabs/CustomTabPanel';
import { useConsultHistory } from '../../shared/History/consultHistorySetup';
import ConsultHistory from '@qler-view/shared_lib/src/shared/History/ConsultHistory';
import { CallStatusEnum } from '../../shared/CallStatusEnum';
import CustomBadge from '@qler-view/shared_lib/src/shared/lib/ui-components/Badge/CustomBadge';
import { formatFieldValue } from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';

const ConsultView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId') ?? '0';
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<number | string>(
    location.state?.cameFromBack ? 1 : 0
  );
  const { consultId } = useParams<{ consultId?: string }>();
  const { ConsultViewDetailsById, isLoading } = useGetConsultViewDetailsById(
    userId,
    Number(consultId)
  );

  const user = getUserFromStorage();
  const { acsChatToken } = useGetAcsChatToken(
    user?.userId ?? 0,
    PORTAL_TYPES.OPERATIONS
  );
  const { consultChatThread } = useGetConsultChatThread(
    Number(consultId) || 0,
    user?.userId ?? 0,
    PORTAL_TYPES.OPERATIONS,
    isChatOpen
  );

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number | string
  ) => {
    setActiveTab(newValue);
  };

  const handleEditConsultInfo = (consultId: number) => {
    navigate(NEW_CONSULTS_ROUTE_PATH + `/${consultId}`, {
      replace: true,
      state: { consultId: consultId },
    });
  };

  const tabItems = [{ label: 'History', value: 0 }];

  const { consultHistory, isLoading: historyLoading } = useConsultHistory(
    Number(consultId),
    PORTAL_TYPES.OPERATIONS
  );

  const setViewOnly = (status?: string) => {
    const normalizedStatus = status?.toLowerCase();
    const isClosed =
      normalizedStatus === CallStatusEnum.Completed.toLowerCase() ||
      normalizedStatus === CallStatusEnum.Cancelled.toLowerCase();

    return isClosed;
  };

  const handleChatToggle = () => {
    setIsChatOpen((prev) => !prev);
  };

  useEffect(() => {
    if (location.state?.chatOpen) {
      setIsChatOpen(true);
    }
  }, [location.state?.chatOpen]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }
  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="flex-justify-space-between align-items-baseline">
              <Box className="text-align-left display-flex">
                <CustomIconButton
                  id="back-button"
                  icon={<BackIcon />}
                  onClick={() => {
                    navigate(CONSULTS_ROUTE_PATH);
                  }}
                ></CustomIconButton>
                <CustomTypography
                  className="pl-12"
                  variant="h4"
                  id="partner-business-id"
                >
                  {consultId
                    ? `View Consult - (${ConsultViewDetailsById?.sessionId})`
                    : 'View Consult'}
                </CustomTypography>
              </Box>

              <Box className="flex align-items-center">
                <Box className="mr-12">
                  <CustomBadge
                    id={`${ConsultViewDetailsById?.id}-chat-notification`}
                    badgeContent={ConsultViewDetailsById?.unreadMessageCount}
                    color="error"
                    max={99}
                  >
                    <CustomIconButton
                      id="chat-icon"
                      size="small"
                      icon={<ChatIcon />}
                      onClick={handleChatToggle}
                    />
                  </CustomBadge>
                </Box>
                <Box>
                  <CustomButton
                    id="save-button"
                    onClick={() => {
                      handleEditConsultInfo(ConsultViewDetailsById?.id || 0);
                    }}
                    variant="contained"
                  >
                    Edit
                  </CustomButton>
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box>
        <Grid2 container spacing={3} className="width-100">
          <Grid2
            size={{
              xs: 12,
              sm: 12,
              md: isChatOpen ? 8 : 12,
              lg: isChatOpen ? 8 : 12,
              xl: isChatOpen ? 8 : 12,
            }}
          >
            <Box className="form-background p-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-32"
                    id="consult-info-text"
                  >
                    Consult Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="facility-name-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Facility Name
                    </CustomTypography>
                    <CustomTypography
                      id="partner-name-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {ConsultViewDetailsById?.facilityName || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="consult-status-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Consult Status
                    </CustomTypography>
                    <CustomTypography
                      id="consult-status-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {ConsultViewDetailsById?.sessionStatus || 'N/A'}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="assigned-to-label"
                      variant="body3"
                      color="textDisabled"
                      className="text-align-left pb-12"
                    >
                      Assigned To
                    </CustomTypography>
                    <CustomTypography
                      id="assigned-to-value"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left"
                    >
                      {ConsultViewDetailsById?.consultAssignedToProviderId ||
                        'N/A'}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="consult-fields-text"
                    >
                      Consult Fields
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  {ConsultViewDetailsById?.sessionFields.map((field) => (
                    <Grid2
                      key={field.fieldName}
                      size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
                    >
                      <CustomTypography
                        id={`field-label-${field.fieldName}`}
                        variant="body3"
                        color="textDisabled"
                        className="text-align-left pb-12"
                      >
                        {field.fieldLabel}
                      </CustomTypography>
                      <CustomTypography
                        id={`field-value-${field.fieldName}`}
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left"
                      >
                        {formatFieldValue(field.fieldValue.toString(), {
                          isDateOfBirth: field.fieldLabel === 'Date Of Birth',
                        })}
                      </CustomTypography>
                    </Grid2>
                  ))}
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="additional-consult-fields-text"
                    >
                      Additional Information
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  {ConsultViewDetailsById?.additionalSessionFields.map(
                    (field) => (
                      <Grid2
                        key={field.fieldName}
                        size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
                      >
                        <CustomTypography
                          id={`field-label-${field.fieldName}`}
                          variant="body3"
                          color="textDisabled"
                          className="text-align-left pb-12"
                        >
                          {field.fieldLabel}
                        </CustomTypography>
                        <CustomTypography
                          id={`field-value-${field.fieldName}`}
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left"
                        >
                          {formatFieldValue(field.fieldValue?.toString())}
                        </CustomTypography>
                      </Grid2>
                    )
                  )}
                </Grid2>
              </Box>
            </Box>

            <Box className="pt-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12 }} className="pb-24">
                  <CustomCard id="facility-details-tab" className="width-100">
                    <CustomCardContent
                      id="facility-details-tab-content"
                      className="card-content"
                    >
                      <Box>
                        <CustomTabs
                          id="facility-more-details-tab"
                          tabs={tabItems}
                          value={activeTab}
                          onChange={handleTabChange}
                          indicatorColor="primary"
                          textColor="primary"
                          variant="scrollable"
                          allowScrollButtonsMobile={true}
                          orientation="horizontal"
                        />
                      </Box>
                      <CustomDivider />
                      <Box>
                        <CustomTabPanel value={activeTab} index={0}>
                          <CustomTypography id="" variant="body2">
                            <Box>
                              <Grid2 container>
                                <Grid2
                                  size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                    xl: 12,
                                  }}
                                  className="text-left"
                                >
                                  {consultHistory && (
                                    <ConsultHistory
                                      consultHistory={consultHistory}
                                      isLoading={historyLoading}
                                    />
                                  )}
                                  {!consultHistory && !historyLoading && (
                                    <Box>No consultation history found</Box>
                                  )}
                                  {historyLoading && <Box>Loading...</Box>}
                                </Grid2>
                              </Grid2>
                            </Box>
                          </CustomTypography>
                        </CustomTabPanel>
                      </Box>
                    </CustomCardContent>
                  </CustomCard>
                </Grid2>
              </Grid2>
            </Box>
          </Grid2>
          <Grid2
            size={{
              xs: 12,
              sm: 12,
              md: isChatOpen ? 4 : 0,
              lg: isChatOpen ? 4 : 0,
              xl: isChatOpen ? 4 : 0,
            }}
          >
            <Box className="pb-24">
              <ChatQLER
                onClose={() => setIsChatOpen(false)}
                acsConfig={
                  acsChatToken
                    ? {
                        endpoint: import.meta.env.VITE_ACS_ENDPOINT,
                        token: acsChatToken.token,
                        userId: acsChatToken.acsUserId,
                        displayName:
                          `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ??
                          user?.username ??
                          'User',
                        portalType: PORTAL_TYPES.OPERATIONS,
                      }
                    : undefined
                }
                isViewOnly={setViewOnly(ConsultViewDetailsById?.sessionStatus)}
                threadId={consultChatThread?.threadId}
                subtitle={consultChatThread?.participants || ''}
                hidden={!isChatOpen}
              />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default ConsultView;
